import React from "react";
import {
  Box,
  styled,
  Button,
  TextField,
  Snackbar,
  Switch,
  Select,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import SlotManagementCreateController, {
  Props,
  S,
  SLOT_TYPES,
  IMobileService,
  ISlotOverride,
} from "./SlotManagementCreateController.web";

import { CustomSearchableMultiSelect } from "../../../components/src/customComponent/CustomSearchableMultiSelect.web";
import CustomLoader from "../../../components/src/customComponents/CustomLoader.web";
import { colors } from "../../utilities/src/Colors";

const Strings = {
  pageTitle: "Slot Management",
  create: "Create",
  edit: "Edit",
  save: "Save",
  update: "Update",
  cancel: "Cancel",
  mobileService: "Mobile Service",
  threshold: "Threshold",
  activated: "Activated",
  region: "Region",
  area: "Area",
  store: "Store",
  slots: "Slot Schedule",
  slotType: "Slot Type",
  interval: "Interval (minutes)",
  enterInterval: "e.g. 30",
  selectMobileService: "Select Mobile Service",
  enterThreshold: "Enter threshold",
  selectRegion: "Select Region",
  selectArea: "Select Area",
  selectStore: "Select Store",
  startTime: "Start Time",
  endTime: "End Time",
  slotOverrides: "Slot Overrides",
  addOverride: "Add Override",
  startDate: "Start Date",
  endDate: "End Date",
  reason: "Reason",
  enterReason: "Enter reason (e.g. Holiday)",
};

export default class SlotManagementCreate extends SlotManagementCreateController {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      isSaving: false,
      saveStatus: "",
      mobile_service_id: null,
      threshold: "",
      activated: true,
      mobileServiceList: [],
      regionList: [],
      areaList: [],
      storeList: [],
      regionSelectedData: [],
      selectedRegionsForList: [],
      isRegionSelectAll: false,
      areaSelectedData: [],
      selectedAreasForList: [],
      isAreaSelectAll: false,
      storeSelectedData: [],
      selectedStoresForList: [],
      isStoreSelectAll: false,
      slot_type: "pickup",
      start_time: "",
      end_time: "",
      interval_minutes: "",
      slotOverrides: [],
      error: {},
    } as S;
  }

  renderSlotSchedule() {
    const { slot_type, start_time, end_time, interval_minutes, error } = this.state;
    const windows = this.getGeneratedWindows();

    return (
      <SlotsContainer>
        <SlotRow>
          <div style={{ flex: 1 }}>
            <TextLabel>{Strings.slotType}</TextLabel>
            <Select
              data-test-id="slotTypeSelect"
              fullWidth
              variant="outlined"
              value={slot_type}
              onChange={(event) =>
                this.updateWindowField("slot_type", event.target.value as string)
              }
              error={Boolean(error.slot_type)}
            >
              {SLOT_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </div>
          <TextField
            data-test-id="windowStartTime"
            type="time"
            variant="outlined"
            size="small"
            label={Strings.startTime}
            value={start_time}
            onChange={(event) =>
              this.updateWindowField("start_time", event.target.value)
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            error={Boolean(error.start_time)}
            helperText={error.start_time || ""}
            style={{ flex: 1 }}
          />
          <TextField
            data-test-id="windowEndTime"
            type="time"
            variant="outlined"
            size="small"
            label={Strings.endTime}
            value={end_time}
            onChange={(event) =>
              this.updateWindowField("end_time", event.target.value)
            }
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            error={Boolean(error.end_time)}
            helperText={error.end_time || ""}
            style={{ flex: 1 }}
          />
          <TextField
            data-test-id="windowInterval"
            type="number"
            variant="outlined"
            size="small"
            label={Strings.interval}
            placeholder={Strings.enterInterval}
            value={interval_minutes}
            onChange={(event) =>
              this.updateWindowField("interval_minutes", event.target.value)
            }
            InputLabelProps={{ shrink: true }}
            error={Boolean(error.interval_minutes)}
            helperText={error.interval_minutes || ""}
            style={{ flex: 1 }}
          />
        </SlotRow>

        {/* Integer division truncates: 100 over 7 slots is 14 each, not 14.28. */}
        <SummaryRow data-test-id="windowSummary">
          <span>
            Slots per day: <b>{this.getSlotsPerDay()}</b>
          </span>
          <span>
            Capacity per slot: <b>{this.getPerSlotCapacity()}</b>
          </span>
        </SummaryRow>

        {windows.length > 0 ? (
          <WindowList>
            {windows.map((window) => (
              <WindowChip key={`${window.start}-${window.end}`}>
                {window.start} – {window.end}
              </WindowChip>
            ))}
          </WindowList>
        ) : (
          <NoSlotText>
            Set a start time, end time and interval to generate slots
          </NoSlotText>
        )}
      </SlotsContainer>
    );
  }

  render() {
    const {
      isSaving,
      saveStatus,
      mobileServiceList,
      mobile_service_id,
      threshold,
      activated,
      regionList,
      areaList,
      storeList,
      regionSelectedData,
      selectedRegionsForList,
      areaSelectedData,
      selectedAreasForList,
      storeSelectedData,
      selectedStoresForList,
      error,
    } = this.state;

    return (
      <MainContainer>
        <Snackbar
          open={saveStatus.length > 0}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => this.setState({ saveStatus: "" })}
        >
          <Alert
            onClose={() => this.setState({ saveStatus: "" })}
            severity={
              saveStatus.includes("successfully") ? "success" : "error"
            }
          >
            {saveStatus}
          </Alert>
        </Snackbar>

        <Heading>{this.isEdit ? Strings.edit : Strings.create} {Strings.pageTitle}</Heading>

        {isSaving && <CustomLoader loading={isSaving} />}

        <WhiteContainer>
          <GridContainer>
            <div>
              <TextLabel>{Strings.mobileService} *</TextLabel>
              <Select
                data-test-id="mobileServiceSelect"
                fullWidth
                variant="outlined"
                value={mobile_service_id || ""}
                onChange={(e) =>
                  this.setState({
                    mobile_service_id: Number(e.target.value),
                  })
                }
                displayEmpty
                error={Boolean(error.mobile_service_id)}
                style={{ borderRadius: 8 }}
              >
                <MenuItem value="" disabled>
                  {Strings.selectMobileService}
                </MenuItem>
                {mobileServiceList.map((service: IMobileService) => (
                  <MenuItem
                    key={service.id}
                    value={service.attributes.id}
                  >
                    {service.attributes.name}
                  </MenuItem>
                ))}
              </Select>
              {error.mobile_service_id && (
                <ErrorLabel>{error.mobile_service_id}</ErrorLabel>
              )}
            </div>

            <div>
              <TextLabel>{Strings.threshold} *</TextLabel>
              <StyledTextField
                data-test-id="thresholdInput"
                fullWidth
                variant="outlined"
                type="number"
                value={threshold}
                placeholder={Strings.enterThreshold}
                onChange={(e) => this.setState({ threshold: e.target.value })}
                error={Boolean(error.threshold)}
                helperText={error.threshold || ""}
              />
            </div>

            <div>
              <TextLabel>{Strings.activated}</TextLabel>
              <Switch
                data-test-id="activatedSwitch"
                checked={activated}
                onChange={(e) =>
                  this.setState({ activated: e.target.checked })
                }
                color="primary"
              />
            </div>
          </GridContainer>

          <SectionDivider />

          <GridContainer>
            <div>
              <TextLabel>{Strings.region}</TextLabel>
              <CustomSearchableMultiSelect
                id="region"
                name="region"
                dataTestId="regionDropdown"
                checkSelectAllText={() => {}}
                value={regionSelectedData}
                optionList={regionList.map((r: any) => ({
                  id: r.id,
                  option: r.name,
                }))}
                placeHolderText={Strings.selectRegion}
                changeInputOnClear
                handleScroll={() => {}}
                handleClickOnSelectAll={() => {}}
                isOnBlurFunction={() => {}}
                debouncedFunction={() => {}}
                handleSelectOptions={(values: any[], option: any) => {
                  this.handleRegionMultiSelect(values, option);
                }}
                emptyDataMessage="No Region Found"
                handleEmptyAutoSelectValue={() => {}}
                selectedOptionForList={selectedRegionsForList}
                isError={false}
              />
            </div>

            <div>
              <TextLabel>{Strings.area}</TextLabel>
              <CustomSearchableMultiSelect
                id="area"
                name="area"
                dataTestId="areaDropdown"
                checkSelectAllText={() => {}}
                value={areaSelectedData}
                optionList={areaList.map((a: any) => ({
                  id: a.id,
                  option: a.name,
                }))}
                placeHolderText={Strings.selectArea}
                changeInputOnClear
                handleScroll={() => {}}
                handleClickOnSelectAll={() => {}}
                isOnBlurFunction={() => {}}
                debouncedFunction={() => {}}
                handleSelectOptions={(values: any[], option: any) => {
                  this.handleAreaMultiSelect(values, option);
                }}
                emptyDataMessage="No Area Found"
                handleEmptyAutoSelectValue={() => {}}
                selectedOptionForList={selectedAreasForList}
                isError={false}
              />
            </div>

            <div>
              <TextLabel>{Strings.store}</TextLabel>
              <CustomSearchableMultiSelect
                id="store"
                name="store"
                dataTestId="storeDropdown"
                checkSelectAllText={() => {}}
                value={storeSelectedData}
                optionList={storeList.map((s: any) => ({
                  id: s.id,
                  option: s.store_name || s.name,
                }))}
                placeHolderText={Strings.selectStore}
                changeInputOnClear
                handleScroll={() => {}}
                handleClickOnSelectAll={() => {}}
                isOnBlurFunction={() => {}}
                debouncedFunction={() => {}}
                handleSelectOptions={(values: any[], option: any) => {
                  this.handleStoreMultiSelect(values, option);
                }}
                emptyDataMessage="No Stores Found"
                handleEmptyAutoSelectValue={() => {}}
                selectedOptionForList={selectedStoresForList}
                isError={false}
              />
            </div>
          </GridContainer>

          <SectionDivider />

          <TextLabel style={{ fontSize: 20, marginBottom: 16 }}>
            {Strings.slots}
          </TextLabel>
          {this.renderSlotSchedule()}

          <SectionDivider />

          <OverrideHeader>
            <TextLabel style={{ fontSize: 20, marginBottom: 0 }}>
              {Strings.slotOverrides}
            </TextLabel>
            <Button
              data-test-id="addOverrideBtn"
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => this.addSlotOverride()}
              style={{ color: colors().cyancobaltblue, borderColor: colors().cyancobaltblue, textTransform: "unset" }}
            >
              {Strings.addOverride}
            </Button>
          </OverrideHeader>

          {this.state.slotOverrides.map((override: ISlotOverride, index: number) => (
            <OverrideCard key={index}>
              <OverrideRow>
                <TextField
                  data-test-id={`overrideStartDate-${index}`}
                  type="date"
                  variant="outlined"
                  size="small"
                  label={Strings.startDate}
                  value={override.start_date}
                  onChange={(e) => this.updateSlotOverride(index, "start_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(this.state.error[`override_${index}_start_date`])}
                  helperText={this.state.error[`override_${index}_start_date`] || ""}
                  style={{ flex: 1 }}
                />
                <TextField
                  data-test-id={`overrideEndDate-${index}`}
                  type="date"
                  variant="outlined"
                  size="small"
                  label={Strings.endDate}
                  value={override.end_date}
                  onChange={(e) => this.updateSlotOverride(index, "end_date", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(this.state.error[`override_${index}_end_date`])}
                  helperText={this.state.error[`override_${index}_end_date`] || ""}
                  style={{ flex: 1 }}
                />
                <TextField
                  data-test-id={`overrideStartTime-${index}`}
                  type="time"
                  variant="outlined"
                  size="small"
                  label={Strings.startTime + " (Optional)"}
                  value={override.start_time}
                  onChange={(e) => this.updateSlotOverride(index, "start_time", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  style={{ flex: 1 }}
                />
                <TextField
                  data-test-id={`overrideEndTime-${index}`}
                  type="time"
                  variant="outlined"
                  size="small"
                  label={Strings.endTime + " (Optional)"}
                  value={override.end_time}
                  onChange={(e) => this.updateSlotOverride(index, "end_time", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  style={{ flex: 1 }}
                />
              </OverrideRow>
              <OverrideRow>
                <TextField
                  data-test-id={`overrideReason-${index}`}
                  variant="outlined"
                  size="small"
                  label={Strings.reason}
                  placeholder={Strings.enterReason}
                  value={override.reason}
                  onChange={(e) => this.updateSlotOverride(index, "reason", e.target.value)}
                  error={Boolean(this.state.error[`override_${index}_reason`])}
                  helperText={this.state.error[`override_${index}_reason`] || ""}
                  style={{ flex: 1 }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <TextLabel style={{ paddingBottom: 0 }}>{Strings.activated}</TextLabel>
                  <Switch
                    data-test-id={`overrideActivated-${index}`}
                    checked={override.activated}
                    onChange={(e) => this.updateSlotOverride(index, "activated", e.target.checked)}
                    color="primary"
                    size="small"
                  />
                </div>
                <IconButton
                  data-test-id={`removeOverride-${index}`}
                  size="small"
                  onClick={() => this.removeSlotOverride(index)}
                  style={{ color: "#EC4E20" }}
                >
                  <DeleteIcon />
                </IconButton>
              </OverrideRow>
            </OverrideCard>
          ))}

          {this.state.slotOverrides.length === 0 && (
            <NoSlotText>No slot overrides added</NoSlotText>
          )}
        </WhiteContainer>

        <BottomContainer>
          <button
            data-test-id="btn-cancel"
            onClick={() => this.clickCancel()}
            className="cancel"
          >
            {Strings.cancel}
          </button>
          <button
            data-test-id="btn-submit"
            className="add"
            onClick={() => this.onSubmit()}
            disabled={isSaving}
          >
            {this.isEdit ? Strings.update : Strings.save}
          </button>
        </BottomContainer>
      </MainContainer>
    );
  }
}

// Customizable Area Start
const MainContainer = styled(Box)({
  paddingTop: "25px",
});

const Heading = styled("h2")({
  color: "#000",
  fontSize: 24,
  fontWeight: 600,
  paddingBottom: 25,
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const WhiteContainer = styled("div")({
  borderRadius: 12,
  background: "#FFF",
  padding: "24px 32px",
  boxShadow:
    "0px 8px 32px 0px rgba(0, 0, 0, 0.06), 0px 4px 8px 0px rgba(0, 0, 0, 0.03)",
});

const GridContainer = styled("div")({
  display: "grid",
  gridGap: 32,
  gridTemplateColumns: "1fr 1fr 1fr",
  "@media only screen and (max-width: 1024px)": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const SectionDivider = styled("hr")({
  border: "none",
  borderTop: "1px solid #E2E8F0",
  margin: "24px 0",
});

const TextLabel = styled("label")({
  display: "block",
  fontSize: 16,
  lineHeight: "24px",
  fontWeight: 600,
  paddingBottom: 4,
  color: "var(--neutrals-cool-gray-700, #334155)",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "13px",
  },
});

const ErrorLabel = styled("div")({
  fontSize: 14,
  marginTop: 6,
  fontWeight: 500,
  color: "#EC4E20",
  lineHeight: "22px",
  letterSpacing: "0.25px",
  fontFamily: "Montserrat",
});

const StyledTextField = styled(TextField)({
  borderRadius: 8,
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
  },
  "@media only screen and (max-width: 1024px)": {
    "& .MuiInputBase-input": {
      fontSize: "12px",
      padding: "14.9px 14px",
    },
  },
});

const SlotsContainer = styled("div")({
  display: "grid",
  gridGap: 16,
  gridTemplateColumns: "1fr",
});

const SlotRow = styled("div")({
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  marginBottom: 8,
});

const SummaryRow = styled("div")({
  display: "flex",
  gap: 24,
  flexWrap: "wrap",
  fontSize: 14,
  color: "#475569",
  fontFamily: "Montserrat",
});

const WindowList = styled("div")({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
});

const WindowChip = styled("div")({
  border: "1px solid #E2E8F0",
  borderRadius: 999,
  padding: "4px 12px",
  fontSize: 13,
  color: "#204B9C",
  background: "#F8FAFC",
  fontFamily: "Montserrat",
});

const NoSlotText = styled("div")({
  fontSize: 13,
  color: "#94A3B8",
  fontStyle: "italic",
});

const OverrideHeader = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 16,
});

const OverrideCard = styled("div")({
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  padding: 16,
  marginBottom: 12,
});

const OverrideRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 8,
  flexWrap: "wrap" as "wrap",
});

const BottomContainer = styled("div")({
  gap: 32,
  display: "flex",
  paddingTop: 48,
  "& > button": {
    fontSize: 16,
    padding: 16,
    minWidth: 126,
    border: "none",
    fontWeight: 700,
    borderRadius: 8,
    cursor: "pointer",
    lineHeight: "24px",
    alignItems: "center",
    display: "inline-flex",
    justifyContent: "center",
    fontFamily: "Montserrat",
    "&:disabled": {
      opacity: 0.5,
    },
  },
  "& > .add": {
    color: "white",
    background: "var(--primary, #204B9C)",
    "@media only screen and (max-width: 1024px)": {
      width: "126px",
      height: "44px",
    },
  },
  "& > .cancel": {
    color: "#64748B",
    background: "var(--primary, #F1F5F9)",
    "@media only screen and (max-width: 1024px)": {
      width: "126px",
      height: "44px",
    },
  },
});
// Customizable Area End
